import React from 'react';
import { Pressable, Platform } from 'react-native';
import { useRouter, usePathname, type Href } from 'expo-router';
import { useNavigation, useRoute } from 'expo-router/react-navigation';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Text } from '@/components/gluestack/text';
import { ArrowLeft01Icon } from '@hugeicons-pro/core-stroke-rounded';
import ThemeSwitcher from '@/components/ui/ThemeSwitcher';
import LanguageSelector from '@/components/ui/LanguageSelector';
import { Image } from 'expo-image';
import { useOpenWebPage } from '@/hooks/useOpenWebPage';
import {
  SlideMenu,
  SlideMenuItem,
  SlideMenuItemLabel,
  SlideMenuSeparator,
} from '@/components/ui/slide-menu';
import { Menu01Icon } from '@hugeicons-pro/core-solid-rounded';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import AboutDialog from '@/components/ui/AboutDialog';
import { useTranslation } from 'react-i18next';
import { SUPPORT_URLS } from '@/constants/urls';

export interface ICommunitiesHeaderProps {
  title: string;
  backHref?: Href;
  headerClassName?: string;
  titleClassName?: string;
  iconClassName?: string;
  rightIconMode?: boolean;
  rightNeutral?: boolean;
  hideBackOnRoot?: boolean;
  leftNode?: React.ReactNode;
  showRightIcons?: boolean;
}

export function CommunitiesHeader({
  title,
  backHref = '/communities',
  headerClassName = 'bg-neutral-800',
  titleClassName = 'color-white',
  iconClassName = 'color-white',
  rightIconMode = true,
  rightNeutral = true,
  hideBackOnRoot = false,
  leftNode,
  showRightIcons = true,
}: ICommunitiesHeaderProps) {
  const router = useRouter();
  const pathname = usePathname();
  const navigation = useNavigation();
  const route = useRoute();
  const insets = useSafeAreaInsets();
  const top = insets.top;
  const openWebPage = useOpenWebPage();
  const { t } = useTranslation();
  const [isAboutOpen, setIsAboutOpen] = React.useState(false);

  const canGoBack =
    typeof (navigation as any).canGoBack === 'function' ? (navigation as any).canGoBack() : false;

  const isCurrentRouteFirst = (() => {
    try {
      const state: any = (navigation as any).getState?.();
      const routes = state?.routes as Array<{ key: string }> | undefined;
      if (!routes) return false;
      const indexInRoutes = routes.findIndex((r) => r.key === (route as any).key);
      return indexInRoutes === 0;
    } catch {
      return false;
    }
  })();

  // On web at /communities, always show the Grokio logo instead of a back button
  const isWebCommunitiesRoot =
    Platform.OS === 'web' && pathname && pathname.replace(/\/+$/, '') === '/communities';

  const showBackButton =
    !isWebCommunitiesRoot && canGoBack && !(hideBackOnRoot && isCurrentRouteFirst);

  return (
    <VStack className={headerClassName} style={{ paddingTop: top }}>
      <HStack className="mx-auto w-full max-w-7xl items-center justify-between px-4 py-3">
        <HStack className="items-center gap-3">
          {leftNode ? (
            <>
              {leftNode}
              <Text size="xl" bold className={titleClassName}>
                {title}
              </Text>
            </>
          ) : showBackButton ? (
            <>
              <Pressable
                onPress={() => {
                  if (canGoBack) (navigation as any).goBack();
                  else router.replace(backHref);
                }}
                hitSlop={10}>
                <HugeIconsWrapper icon={ArrowLeft01Icon} size={28} className={iconClassName} />
              </Pressable>
              <Text size="xl" bold className={titleClassName}>
                {title}
              </Text>
            </>
          ) : (
            <Pressable
              onPress={() => openWebPage('https://www.grokio.com')}
              accessibilityRole={Platform.OS === 'web' ? 'link' : 'button'}
              hitSlop={6}>
              <Image
                source={require('@/assets/logos/grokio.png')}
                className="h-8 w-32"
                contentFit="contain"
                alt="Grokio Communities"
              />
            </Pressable>
          )}
        </HStack>
        {showRightIcons && (
          <HStack className="items-center gap-3">
            <ThemeSwitcher size="sm" neutral={rightNeutral} iconOnly={rightIconMode} />
            <LanguageSelector size="sm" neutral={rightNeutral} iconOnly={rightIconMode} />
            {rightIconMode && (
              <SlideMenu
                placement="bottom right"
                offset={12}
                trigger={(triggerProps) => (
                  <Pressable {...triggerProps} hitSlop={6} accessibilityRole="button">
                    <HugeIconsWrapper
                      icon={Menu01Icon}
                      size={22}
                      className={rightNeutral ? 'color-typography-500' : 'color-white'}
                    />
                  </Pressable>
                )}>
                <SlideMenuItem
                  key="support"
                  onPress={() => {
                    setTimeout(() => openWebPage(SUPPORT_URLS.atlassian.support.helpCenter), 50);
                  }}>
                  <SlideMenuItemLabel>{t('footer.support')}</SlideMenuItemLabel>
                </SlideMenuItem>
                <SlideMenuItem
                  key="terms"
                  onPress={() => {
                    setTimeout(() => openWebPage(SUPPORT_URLS.atlassian.legal.termsOfUse), 50);
                  }}>
                  <SlideMenuItemLabel>{t('footer.terms')}</SlideMenuItemLabel>
                </SlideMenuItem>
                <SlideMenuItem
                  key="privacy"
                  onPress={() => {
                    setTimeout(() => openWebPage(SUPPORT_URLS.atlassian.legal.privacyPolicy), 50);
                  }}>
                  <SlideMenuItemLabel>{t('footer.privacy')}</SlideMenuItemLabel>
                </SlideMenuItem>
                <SlideMenuSeparator />
                <SlideMenuItem
                  key="about"
                  onPress={() => {
                    setTimeout(() => setIsAboutOpen(true), 50);
                  }}>
                  <SlideMenuItemLabel>{t('settings.about.title')}</SlideMenuItemLabel>
                </SlideMenuItem>
              </SlideMenu>
            )}
          </HStack>
        )}
      </HStack>
      <AboutDialog isOpen={isAboutOpen} onClose={() => setIsAboutOpen(false)} />
    </VStack>
  );
}

export const createCommunitiesHeader = (props: ICommunitiesHeaderProps) => () => (
  <CommunitiesHeader {...props} />
);
